// Everything the plan loader reports about a document, in one place. Split out of
// constraints.ts, which is the sample-rate-dependent feature limits and nothing else:
// a rate limit is a warning about a plan the app itself authored, while these are the
// checks a plan built ELSEWHERE (a file, a ?plan= link, a generator) has to pass.
// routing.ts cannot host them — constraints -> translate -> routing is a real
// dependency chain — so they live here. Language-agnostic: the UI maps codes to
// messages. Nothing here runs on a device readback (see insertFxSlotProblems).

import type { DeviceModel } from "../models/types";
import { parseRef, ref } from "../models/types";
import { factoryNodeColors, factoryNodeNames, factoryNodeParams, fillFactoryParams } from "../models/initial-state";
import { getModel, MODEL_IDS } from "../models";
import { insertFxCensus } from "./constraints";
import { FX_CHANNEL_NODE_INDEX, fxEffectTypes, fxParams, fxRawForDesc } from "./control/fx-effect";
import {
  COMP_EQ_COMP_FIRST,
  COMP_ONE_KNOB_HELD,
  COMP_ONE_KNOB_ON,
  isPlanColor,
  type InsertFxSlot,
} from "./control/params";
import {
  fixedConnection,
  isPlainRecord,
  jackKey,
  normalizeDocumentName,
  requiredSourceWire,
  SEND_LEVEL_UNNAMED_DB,
  setPlanSampleRate,
  stripXmlInvalid,
} from "./plan";
import {
  connParamContestKey,
  deepEqual,
  nodeColorContestKey,
  nodeNameContestKey,
  nodeParamContestPath,
} from "./plan-history";
import type { ConnParams, NodeParams, Plan, PlanConnection } from "./plan";
import {
  admitLeaf,
  channelDynamics,
  colorControl,
  effectiveInsertFx,
  insertFxControl,
  insertFxWireState,
  nameControl,
  nodeLeafRules,
  sendControl,
} from "./control/translate";
import {
  insertFxDefaults,
  insertFxDriverOn,
  insertFxFamilyOf,
  insertFxSlotRaw,
  insertFxWritableSlots,
  MBC_ONE_KNOB,
  MBC_ONE_KNOB_FIXED,
  reKeyInsertFxParams,
  seedInsertFxParams,
} from "./control/insert-fx-effect";
import { hiZOn } from "./input-lock";
import {
  INSERT_FX_PAIR_KEYS,
  isBalLinkedPair,
  isStereoLinkedPair,
  mixSendLocks,
  pairSharesNodeKey,
  sourcePan,
  validatePlan,
} from "./routing";
import type { PlanProblem } from "./routing";

/** One device-wide 1-of insert-FX slot claimed by more than one node. Not a wire,
 *  so it carries the contended slot and its holders instead of two endpoints. */
export interface InsertFxSlotProblem {
  reason: "insertFxSlot";
  slot: InsertFxSlot;
  /** Every node whose selection claims the slot, in model order (two or more). */
  nodes: string[];
}

// Insert-FX slot collisions in a whole plan. The screens cannot author one
// (insertFxMenu locks a slot another node holds), so a plan carrying one came
// from outside — a file, a ?plan= link, a generator, or a read of a unit that holds
// two, since its control link accepts a second selection. Unlike an illegal wire this
// does not refuse the document: the loader reports it and offers to open it anyway,
// since the plan is otherwise usable and only the unit decides what it runs.
// A device readback deliberately does not run this: the unit is the authority for
// what it is actually running, and refusing there would leave the operator unable
// to read the hardware at all.
export function insertFxSlotProblems(model: DeviceModel, plan: Plan): InsertFxSlotProblem[] {
  return [...insertFxCensus(model, plan)]
    .filter(([, nodes]) => nodes.length > 1)
    .map(([slot, nodes]) => ({ reason: "insertFxSlot" as const, slot, nodes: [...nodes] }));
}

/** A STEREO-linked MONO IN pair whose two members do not agree about their one insert
 *  effect. Carries the pair and which of `INSERT_FX_PAIR_KEYS` disagree. */
export interface InsertFxPairProblem {
  reason: "insertFxPair";
  /** The pair, primary first. */
  nodes: [string, string];
  /** The stored keys whose written state disagrees, selector first. */
  keys: string[];
}

/** Which stored key each half of the wire state came from, so the report names what the
 *  author wrote rather than what the wire calls it. */
const PAIR_STATE_KEYS = { selector: "insertFx", on: "insertFxOn", params: "insertFxParams" } as const;

/**
 * A STEREO-linked pair holds ONE insert effect between its two channels: the unit keeps a
 * single selector, one bypass and one engine for the pair and mirrors a write to either
 * member onto the other. The app's own edit funnels keep the two sides equal
 * (`mirrorLinkedInsertFx`), but a document built elsewhere is under no such discipline, and
 * nothing downstream repairs it — `translate` emits each channel from its own params, so a
 * disagreeing pair sends two different selectors, the unit keeps whichever landed last, and
 * `sendConverging` re-sends both to its round limit and finishes unconverged.
 *
 * So it is a refusal rather than a warning: there is no state of the unit that satisfies the
 * document, and the operator has nothing to decide.
 *
 * What is compared is the state a WRITE would leave, not what the document stores. The two
 * are different questions, and stored values answer the wrong one: an off-menu selector and
 * No Effect reach the unit as one value, `true` and `1` are one bypass, a bypass beside No
 * Effect is never sent, and an engine value under another family's namespace is not sent
 * either. Each of those is a document the unit CAN satisfy, and refusing it would be a load
 * the operator cannot explain. `insertFxWireState` is that projection and lives in the emit
 * path, so it and the write cannot drift apart; the plan is filled first because the write
 * sees a completed document.
 */
export function insertFxPairProblems(model: DeviceModel, plan: Plan): InsertFxPairProblem[] {
  const out: InsertFxPairProblem[] = [];
  const linked = model.channelPairs.filter(([a]) => plan.nodeParams[a]?.stereoLink === true);
  if (linked.length === 0) return out;
  // The fill mutates, so it runs on a copy: this is a question about the document, and
  // answering it must not complete the caller's plan behind their back.
  const filled = structuredClone(plan);
  fillFactoryParams(model.id, filled);
  completeInsertFxParams(model, filled);
  for (const [a, b] of linked) {
    const sa = insertFxWireState(model, filled, a);
    const sb = insertFxWireState(model, filled, b);
    if (!sa || !sb) continue;
    const keys: string[] = [];
    if (sa.selector !== sb.selector) keys.push(PAIR_STATE_KEYS.selector);
    if (sa.on !== sb.on) keys.push(PAIR_STATE_KEYS.on);
    if (sa.params.join("|") !== sb.params.join("|")) keys.push(PAIR_STATE_KEYS.params);
    if (keys.length > 0) out.push({ reason: "insertFxPair", nodes: [a, b], keys });
  }
  return out;
}

/** One stored parameter outside the range its own control admits. `translate.ts` bounds
 *  every FX slot to the same rawMin/rawMax the slider declares, so a document holding one
 *  means a different thing on screen than on the wire: the panel shows what the plan holds
 *  and the unit receives the bound. The loader normalizes the document to the bound and
 *  says so, which is the only reading under which the two agree.
 *
 *  A plan the app itself authored cannot carry one — the sliders stop at the window and the
 *  write bounds anything else — so this is a document from a file an older build saved, a
 *  hand-edited one, or a `?plan=` payload. Like every check in this file it does NOT run on
 *  a device readback: the unit is the authority for what it is running, and it can hold a
 *  raw this app's range excludes, because its own encoder stops at the window but the wire
 *  does not. A `.urxf` import takes the same exemption for the same reason and by the same
 *  route — it is a FILE, but one the unit wrote, and it reaches the plan through the readback
 *  rather than through this funnel.
 *
 *  SCOPE: the FX channel effect; on every node the model's factory values describe, a leaf or
 *  group whose kind is not the factory value's at that path (`where: "node"`, dropped, so the
 *  fill supplies the factory value) — a number where the factory holds an on/off is converted
 *  instead (`booleanParamProblems`); every node-param leaf the write bounds, bounded by the rule
 *  the write bounds it by (`nodeLeafRules` / `admitLeaf` in translate.ts, `where: "node"`); plus
 *  +48V on a jack with HI-Z on, bounded to off (HI-Z kept) — the app never turns the two on
 *  together. A jack's A.Gain is bounded to its own range (`jackGainRange`: -8..+70 dB, -8..+40
 *  under HI-Z), a source's D.Gain to -24..+24, the oscillator level to -96..0
 *  dB and the HPF frequency to the nearest of its five 20 Hz detents — their encoders clamp
 *  only to a wider window (the HPF's at 0.1 Hz), so here the load moves what the write sends,
 *  onto a value the unit's own panel can set. A device read keeps +48V and HI-Z both on where the unit holds
 *  them, so a file this build saved can carry that pair. A repair reaches the oscillator, which
 *  is scene-external: under the Scene-only device scope the write does not carry it, while the
 *  load still moves it and says so. */
export interface ParamRangeProblem {
  reason: "paramRange";
  node: string;
  /** Which container holds it: the node's `fxEffect` value itself, a field of that object
   *  (`type` / `params`), a member of its `params` map, or one of the node's own params
   *  (`node`). A `params` member is bounded by its own descriptor. */
  where: "effect" | "field" | "params" | "node";
  /** The field or catalogue key, as the plan stores it. `fxEffect` for the object itself. For
   *  `node`, the dotted path inside the node's params, an array element by its index
   *  (`eqOneKnob.level`, `eqBands.0.q`). */
  key: string;
  /** What the document carries. NOT always a number: the sanitiser keeps a boolean leaf and a
   *  non-empty object, since node params have toggles and groups, and a document can put
   *  either under a key that holds a number. */
  stored: unknown;
  /** `bound` writes the value the window admits; `drop` removes the key.
   *
   *  A leaf that is not a finite number is DROPPED rather than replaced by a default, because
   *  the default is the one thing about a key that is not shared: the window is the same under
   *  every type a channel offers (the catalogue pins that), but `FX_TYPE_DEFAULTS` gives each
   *  type its own value. Writing one type's default is therefore a guess about which type is
   *  selected — and the emit already substitutes the SELECTED type's default for a key that is
   *  absent, so dropping it lands on the same value now and stays right if the type changes.
   *
   *  A `type` outside the channel's menu is dropped for a different reason: a menu is a set,
   *  so there is no nearest member to bound to, and `resolveFxEffectType` already answers the
   *  channel's own default for it. Same for the two containers — an `fxEffect` or a `params`
   *  that is not an object holds nothing to bound. All four cases emit exactly what the same
   *  document emits with the key absent, so the drop moves no value; what it changes is that
   *  the plan stops carrying something the write path silently ignores, and the load says so. */
  action: "bound" | "drop";
  /** The value the loader writes. Absent for `drop`, which writes nothing. A boolean only for
   *  a HI-Z channel's `phantom`, which is bounded to false. */
  bound?: number | boolean;
}

/** Every FX parameter a plan holds outside its own control's range, in model order, then the
 *  +48V / A.Gain of each HI-Z channel the SCOPE note above names. Reads the FX type
 *  through `resolveFxEffectType` like every other consumer, so the window
 *  asked about is the one the write path will bound against. */
export function paramRangeProblems(plan: Plan): ParamRangeProblem[] {
  const out: ParamRangeProblem[] = [];
  const take = (
    node: string,
    where: "field" | "params",
    key: string,
    stored: number | undefined,
    normalise: (raw: number) => number,
  ): void => {
    // An ABSENT value is not a problem: the emit substitutes the catalogue default and the
    // panel shows that same default, so the two already agree, and reporting it would write
    // a key the document never carried.
    if (stored === undefined) return;
    // Not a NUMBER at all — a boolean or an object, which the document sanitiser keeps — so
    // there is no value to bound. Dropped rather than defaulted, for the reason on `action`.
    if (typeof stored !== "number" || !Number.isFinite(stored)) {
      out.push({ reason: "paramRange", node, where, key, stored, action: "drop" });
      return;
    }
    // The nearest value the control admits, which is the emit's own answer for it. A raw is a
    // broker integer, so a fractional value names no setting the unit has — and it is not
    // dropped, because unlike a boolean it HAS a numeric neighbour: dropping sends the type's
    // own default instead, which for the delay LPF is 8.50 kHz where the document said 118 Hz.
    //
    // The admitted set is the CONTROL's, not a window: a window is what a slider has, while a
    // toggle admits 0 and 1 with no bounds written down at all and a select admits its option
    // values. Normalising against bounds alone left 2 on a two-state control and 15 on a menu
    // that ends at 14. Which value a key belongs to is the same under every type the channel
    // offers, so whichever type named it, this is the answer the emit will reach.
    const bound = normalise(stored);
    if (bound !== stored) out.push({ reason: "paramRange", node, where, key, stored, action: "bound", bound });
  };
  for (const [node, fxIndex] of Object.entries(FX_CHANNEL_NODE_INDEX)) {
    const fx: unknown = plan.nodeParams[node]?.fxEffect;
    if (fx === undefined) continue;
    // The effect OBJECT. The sanitiser keeps a boolean and a non-empty array of objects, and
    // every reader of the plan treats one as no effect at all — that channel's whole address
    // set the write path then never sends, with the document still holding what it holds.
    if (!isPlainRecord(fx)) {
      out.push({ reason: "paramRange", node, where: "effect", key: "fxEffect", stored: fx, action: "drop" });
      continue;
    }
    // The effect TYPE, against the channel's MENU rather than a window.
    if (fx.type !== undefined && !fxEffectTypes(fxIndex).some((o) => o.value === fx.type)) {
      out.push({ reason: "paramRange", node, where: "field", key: "type", stored: fx.type, action: "drop" });
    }
    // The parameter MAP, which the readers below and the write path both skip when it is not
    // an object — the same silent loss as an unreadable effect, one channel's worth of raws.
    if (fx.params !== undefined && !isPlainRecord(fx.params)) {
      out.push({ reason: "paramRange", node, where: "field", key: "params", stored: fx.params, action: "drop" });
      continue;
    }
    const fxParamsMap = fx.params as Record<string, number> | undefined;
    if (!fxParamsMap) continue;
    // EVERY type the CHANNEL offers, not only the one selected. The migration leaves a key
    // the selected type does not own exactly where it is (fx-effect.ts), so a document saved
    // under another type keeps the raw untouched, and selecting that type later brings it
    // back — the load was the one chance to repair it. The catalogue pins that one channel
    // never gives a key two different windows, so whichever of its types names the key, the
    // window found is the window the write path will bound against.
    const seen = new Set<string>();
    for (const type of fxEffectTypes(fxIndex)) {
      for (const d of fxParams(type.value)) {
        if (seen.has(d.key)) continue;
        seen.add(d.key);
        take(node, "params", d.key, fxParamsMap[d.key], (v) => fxRawForDesc(d, v));
      }
    }
  }
  for (const [node, carried] of Object.entries(plan.nodeParams)) {
    // A leaf or group of a shape the factory value at that path does not have. The checks
    // after it read the node as the drop leaves it.
    const factory = factoryNodeParams(plan.modelId, node);
    const drops = factory ? kindMismatches(carried, factory) : [];
    const np = drops.length ? structuredClone(carried) : carried;
    for (const path of drops) {
      const parent = path.slice(0, -1).reduce<unknown>((v, k) => (v as Record<string, unknown>)[k], np) as Record<
        string,
        unknown
      >;
      const key = path[path.length - 1];
      out.push({ reason: "paramRange", node, where: "node", key: path.join("."), stored: parent[key], action: "drop" });
      delete parent[key];
    }
    // A jack with HI-Z on: its +48V goes off (A.Gain's +40 dB ceiling is in its rule).
    for (const side of ["L", "R"] as const) {
      const key = jackKey("phantom", side);
      if (hiZOn(plan.modelId, { node, side }, np) && np[key]) {
        out.push({ reason: "paramRange", node, where: "node", key, stored: np[key], action: "bound", bound: false });
      }
    }
    // A value outside what the write sends for it: the value the write sends.
    const model = MODEL_IDS.includes(plan.modelId) ? getModel(plan.modelId) : null;
    for (const [path, rule] of model ? nodeLeafRules(model, node, np) : []) {
      const stored = path
        .split(".")
        .reduce<unknown>(
          (v, k) => (isPlainRecord(v) || Array.isArray(v) ? (v as Record<string, unknown>)[k] : undefined),
          np,
        );
      if (typeof stored !== "number" || !Number.isFinite(stored)) continue;
      if ("unsent" in rule) {
        out.push({ reason: "paramRange", node, where: "node", key: path, stored, action: "drop" });
        continue;
      }
      const bound = admitLeaf(rule, stored);
      if (bound !== stored)
        out.push({ reason: "paramRange", node, where: "node", key: path, stored, action: "bound", bound });
    }
  }
  return out;
}

/** The node-param keys whose shape the FX walk owns rather than the factory comparison. */
const KIND_WALK_SKIPS: ReadonlySet<string> = new Set(["fxEffect"]);

/**
 * Every path in `carried` holding a value whose kind is not the factory value's there: a
 * value that is not a number where the factory holds a number, a group where it holds an
 * on/off, and anything but a group (or an array) where it holds one. Neither the sanitiser
 * nor the fill catches these — the sanitiser keeps an on/off and a non-empty group under any
 * key, and the fill keeps whatever the document wrote at a scalar — so the write would encode
 * one (an on/off at A.Gain goes out as +1 dB, a group as the encoder's floor) and a reader
 * that formats a number would throw on it. A path the factory does not carry is left alone.
 */
function kindMismatches(carried: unknown, factory: unknown, path: string[] = []): string[][] {
  if (carried === undefined) return [];
  if (typeof factory === "number") return typeof carried === "number" ? [] : [path];
  if (typeof factory === "boolean") return isPlainRecord(carried) || Array.isArray(carried) ? [path] : [];
  if (Array.isArray(factory)) {
    if (!Array.isArray(carried)) return [path];
    return factory.flatMap((f, i) => kindMismatches(carried[i], f, [...path, String(i)]));
  }
  if (isPlainRecord(factory)) {
    if (!isPlainRecord(carried)) return [path];
    return Object.entries(factory).flatMap(([key, f]) =>
      path.length === 0 && KIND_WALK_SKIPS.has(key) ? [] : kindMismatches(carried[key], f, [...path, key]),
    );
  }
  return [];
}

/** Write each reported bound into the plan. Separate from finding them so a caller can
 *  report without repairing, and so a test can assert the two halves apart. */
export function applyParamRange(plan: Plan, problems: ParamRangeProblem[]): void {
  for (const p of problems) {
    const np = plan.nodeParams[p.node]!;
    if (p.where === "node") {
      const keys = p.key.split(".");
      let holder = np as Record<string, unknown> | undefined;
      for (const key of keys.slice(0, -1)) holder = holder?.[key] as Record<string, unknown> | undefined;
      if (!holder) continue;
      const last = keys[keys.length - 1];
      if (p.action === "drop") delete holder[last];
      else holder[last] = p.bound;
      continue;
    }
    if (p.where === "effect") {
      delete np.fxEffect;
      continue;
    }
    const fx = np.fxEffect!;
    if (p.where === "field") {
      // Every field problem reported today is a drop — the effect TYPE is checked against the
      // channel's menu and the parameter map against its own shape, and neither carries a
      // window — but the bound is applied rather than assumed away, so a field that gains one
      // is repaired instead of being silently removed.
      const rec = fx as unknown as Record<string, unknown>;
      if (p.action === "drop") delete rec[p.key];
      else rec[p.key] = p.bound;
    } else if (p.action === "drop") {
      delete fx.params![p.key];
    } else {
      fx.params![p.key] = p.bound as number;
    }
  }
}

/** A receiver the unit never leaves without a source (`DeviceModel.requiredSources`) that the
 *  document gives no wire. Carries the wire the loader adds: the model's own default source for
 *  that receiver, which is the unit's factory selection.
 *
 *  Completed rather than refused: the unit's list for these offers no "none", so a document
 *  without the wire describes a state the unit cannot be put in from its own panel, and the one
 *  value to give it is the one a new plan starts from. Reported, because the load then writes a
 *  selection the document did not name. Any wire into the receiver counts, whatever kind it is
 *  stored under — the install restates the kind from the rule table — and a wire the sanitiser
 *  dropped does not. Like every check in this file it does NOT run on a device readback: Fetch
 *  and Live start give a unit read on NONE there the same source themselves
 *  (`ReadbackResult.unsourced`). */
export interface RequiredSourceProblem {
  reason: "requiredSource";
  /** The source the loader wires in. */
  from: string;
  /** The receiver's input ref. */
  to: string;
}

/** Every required receiver the plan gives no wire, in the model's order. */
export function requiredSourceProblems(model: DeviceModel, plan: Plan): RequiredSourceProblem[] {
  return Object.entries(model.requiredSources)
    .filter(([to]) => !plan.connections.some((c) => c.to === to))
    .map(([to, from]) => ({ reason: "requiredSource" as const, from, to }));
}

/** Add each reported wire to the plan. Separate from finding them for the reason
 *  `applyParamRange` is. */
export function applyRequiredSources(model: DeviceModel, plan: Plan, problems: RequiredSourceProblem[]): void {
  for (const p of problems) plan.connections.push(requiredSourceWire(model, p.to));
}

/** A fixed send into a MIX or FX bus — one `sendControl` writes, a column of the CONSOLE's send
 *  rack — that the document lists without a level. The write sends it at unity
 *  (`SEND_LEVEL_UNNAMED_DB`), while the send rack and the MIDI feedback read a send with no level
 *  as off — so the loader completes the wire with the level the write sends, records it as the
 *  fill's (the write confirm names the strip when that level would move the unit), and says so.
 *  A main path into STEREO is the channel's fader, which every reader takes at unity without a
 *  level, as the write does, so it is left as written. Like every check in this file it does NOT
 *  run on a device readback or the `.urxf` import, which give every send its level. */
export interface SendLevelProblem {
  reason: "sendLevel";
  /** The send's source, as its out ref. */
  from: string;
  /** The send's destination input ref. */
  to: string;
}

/** Every fixed rack send the document lists with no level, in the document's own order. */
export function sendLevelProblems(model: DeviceModel, plan: Plan): SendLevelProblem[] {
  return plan.connections
    .filter((c) => c.params?.level === undefined && isRackSend(model, c.from, c.to))
    .map((c) => ({ reason: "sendLevel" as const, from: c.from, to: c.to }));
}

/** Whether `from -> to` is a fixed send the write sends a SEND_LEVEL for. */
export function isRackSend(model: DeviceModel, from: string, to: string): boolean {
  return (
    model.rules.some((r) => r.fixed && r.kind === "send" && r.from === from && r.to === to) &&
    sendControl(model, parseRef(from).nodeId, parseRef(to).nodeId) !== null
  );
}

/** Give each reported send the level the write sends, recorded as the fill's. Separate from
 *  finding them for the reason `applyParamRange` is. */
export function applySendLevels(plan: Plan, problems: SendLevelProblem[]): void {
  const source = (plan.paramSource ??= new Map());
  for (const p of problems) {
    const send = plan.connections.find((c) => c.from === p.from && c.to === p.to);
    if (!send) continue;
    send.params = { ...send.params, level: SEND_LEVEL_UNNAMED_DB };
    source.set(connParamContestKey(p.from, p.to, "level"), "default");
  }
}

/** A STEREO-linked MONO IN pair whose members disagree about a value the pair holds once. The
 *  unit keeps one set of values for a linked pair — it copies the odd channel's onto the even one
 *  when the pair is linked, and mirrors a write to either member onto the other — while the write
 *  sends each member from its own params, so a disagreeing pair is two values the unit cannot hold
 *  and a converge that alternates between them. The loader copies the primary's values onto the
 *  secondary, the copy `mirrorLinkedPair` makes on an edit, and says so. A document naming only
 *  the primary is the same repair: the secondary takes the primary's values rather than its own
 *  factory ones.
 *
 *  What is compared is what the write would send: the node params the pair shares
 *  (`pairSharesNodeKey`) as the fill completes them, and each pair of fixed sends into one
 *  destination — level, on/off and PRE/POST, and the pan in BAL, where the pair holds one balance —
 *  read the way the emit reads them, a send the document omits as the one the install seeds. The
 *  insert effect is not: a pair disagreeing about it is refused (`insertFxPairProblems`), and one
 *  agreeing about what the write sends keeps what each member stores.
 *  Like every check in this file it does NOT run on a device readback or the `.urxf` import. */
export interface LinkedPairProblem {
  reason: "linkedPair";
  /** The pair, primary first. */
  nodes: [string, string];
  /** The shared node-param keys whose values disagree, in the primary's key order. */
  keys: string[];
  /** The destinations whose two sends disagree, as input refs, in rule order. */
  sends: string[];
}

const INSERT_FX_KEYS: ReadonlySet<string> = new Set(INSERT_FX_PAIR_KEYS);

/** Whether a linked pair holds `key` once and this check carries it. */
const pairCopies = (key: string): boolean => pairSharesNodeKey(key) && !INSERT_FX_KEYS.has(key);

/** The send params a linked pair holds once: the pan only in BAL. */
const pairSendKeys = (bal: boolean): Array<keyof ConnParams> =>
  bal ? ["level", "on", "tap", "pan"] : ["level", "on", "tap"];

/** The fixed sends both members of a pair have into one destination, as rule pairs. */
function pairSends(model: DeviceModel, a: string, b: string): Array<[RoutingRuleOf, RoutingRuleOf]> {
  const out: Array<[RoutingRuleOf, RoutingRuleOf]> = [];
  for (const ra of model.rules) {
    if (!ra.fixed || ra.kind !== "send" || ra.from !== ref(a, "out")) continue;
    const rb = model.rules.find((r) => r.fixed && r.kind === "send" && r.from === ref(b, "out") && r.to === ra.to);
    if (rb) out.push([ra, rb]);
  }
  return out;
}
type RoutingRuleOf = DeviceModel["rules"][number];

/** A send's params as the write reads them: a listed send's own (a missing level the one the
 *  load completes it with), an omitted one's seed. */
function sentParams(model: DeviceModel, plan: Plan, rule: RoutingRuleOf): Record<string, unknown> {
  const listed = plan.connections.find((c) => c.from === rule.from && c.to === rule.to);
  const params: ConnParams = listed ? (listed.params ?? {}) : (fixedConnection(model, rule).params ?? {});
  return {
    level: params.level ?? SEND_LEVEL_UNNAMED_DB,
    on: params.on ?? true,
    tap: params.tap === "pre" ? "pre" : "post",
    pan: params.pan ?? 0,
  };
}

/** Every STEREO-linked pair whose members disagree, in the model's pair order. */
export function linkedPairProblems(model: DeviceModel, plan: Plan): LinkedPairProblem[] {
  const out: LinkedPairProblem[] = [];
  const linked = model.channelPairs.filter(([a]) => isStereoLinkedPair(model, plan, a));
  if (linked.length === 0) return out;
  // The fill mutates, so it runs on a copy, for the reason insertFxPairProblems gives.
  const filled = structuredClone(plan);
  fillFactoryParams(model.id, filled);
  for (const [a, b] of linked) {
    const pa = (filled.nodeParams[a] ?? {}) as Record<string, unknown>;
    const pb = (filled.nodeParams[b] ?? {}) as Record<string, unknown>;
    const keys = [...new Set([...Object.keys(pa), ...Object.keys(pb)])].filter(
      (k) => pairCopies(k) && !deepEqual(pa[k], pb[k]),
    );
    const sendKeys = pairSendKeys(isBalLinkedPair(model, plan, a));
    const sends = pairSends(model, a, b)
      .filter(([ra, rb]) => {
        const sa = sentParams(model, plan, ra);
        const sb = sentParams(model, plan, rb);
        return sendKeys.some((k) => sa[k] !== sb[k]);
      })
      .map(([ra]) => ra.to);
    if (keys.length > 0 || sends.length > 0) out.push({ reason: "linkedPair", nodes: [a, b], keys, sends });
  }
  return out;
}

/** Copy each reported pair's primary onto its secondary: every node param the pair holds once,
 *  an absent one staying absent, and each pair of sends' shared params — the omitted member of a
 *  send pair given the send the install seeds, so the copy has a wire on both sides. A copied send
 *  param takes the primary's record of where it came from. Separate from finding them for the
 *  reason `applyParamRange` is. */
export function applyLinkedPairs(model: DeviceModel, plan: Plan, problems: LinkedPairProblem[]): void {
  const source = plan.paramSource;
  for (const { nodes } of problems) {
    const [a, b] = nodes;
    const src = (plan.nodeParams[a] ?? {}) as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(plan.nodeParams[b] ?? {})) if (!pairCopies(k)) next[k] = v;
    for (const [k, v] of Object.entries(src)) if (pairCopies(k)) next[k] = structuredClone(v);
    plan.nodeParams[b] = next as NodeParams;
    const sendKeys = pairSendKeys(isBalLinkedPair(model, plan, a));
    for (const [ra, rb] of pairSends(model, a, b)) {
      const find = (r: RoutingRuleOf): PlanConnection | undefined =>
        plan.connections.find((c) => c.from === r.from && c.to === r.to);
      if (!find(ra) && !find(rb)) continue;
      for (const r of [ra, rb]) if (!find(r)) plan.connections.push(fixedConnection(model, r));
      const ca = find(ra)!;
      const cb = find(rb)!;
      const params: ConnParams = { ...cb.params };
      for (const k of sendKeys) {
        const value = ca.params?.[k];
        if (value === undefined) delete params[k];
        else (params as Record<string, unknown>)[k] = value;
        const from = source?.get(connParamContestKey(ca.from, ca.to, k));
        const name = connParamContestKey(cb.from, cb.to, k);
        if (from !== undefined) source?.set(name, from);
        else source?.delete(name);
      }
      cb.params = params;
    }
  }
}

/** A send into a MIX bus whose Pan Link is on, carrying a pan other than its source's own pan /
 *  balance. While Pan Link is on the unit holds every send pan into that MIX there, and the write
 *  sends none of them, so the pan a document carries is one the unit never takes — while the
 *  CONSOLE's read-only SEND PAN knob, the MIDI feedback and the next save all read it. The loader
 *  sets it to the source's value, the one `sendPansToSources` sets when the link turns on, and
 *  says so.
 *
 *  Counted the way every reader counts it: an absent pan — on the send, on the source's main path
 *  into STEREO, or a send or main path the document omits — is 0. A send the document omits is
 *  one the load adds (`ensureFixedConnections`), so it is reported and added here with the pan.
 *  Each member of a STEREO-linked pair takes the pan on its own main path: its own position in
 *  PAN mode, the pair's one balance in BAL.
 *  Like every check in this file it does NOT run on a device readback or the `.urxf` import:
 *  there the unit's own values arrive. */
export interface LinkedSendPanProblem {
  reason: "linkedSendPan";
  /** The send's source, as its out ref. */
  from: string;
  /** The linked MIX's input ref. */
  to: string;
  /** The pan the document carries on the send; absent where it names none, or no send at all. */
  stored?: number;
  /** The source's own pan / balance, which the loader writes. */
  pan: number;
}

/** Every send into a linked MIX whose pan is not its source's, in rule order. */
export function linkedSendPanProblems(model: DeviceModel, plan: Plan): LinkedSendPanProblem[] {
  const out: LinkedSendPanProblem[] = [];
  for (const rule of model.rules) {
    if (!rule.fixed || rule.kind !== "send" || !mixSendLocks(plan, parseRef(rule.to).nodeId).panLinked) continue;
    const stored = plan.connections.find((c) => c.from === rule.from && c.to === rule.to)?.params?.pan;
    const pan = sourcePan(plan, parseRef(rule.from).nodeId);
    if ((stored ?? 0) === pan) continue;
    out.push({
      reason: "linkedSendPan",
      from: rule.from,
      to: rule.to,
      ...(stored === undefined ? {} : { stored }),
      pan,
    });
  }
  return out;
}

/** Set each reported send's pan, adding the send where the document omits it. Separate from
 *  finding them for the reason `applyParamRange` is. */
export function applyLinkedSendPans(model: DeviceModel, plan: Plan, problems: LinkedSendPanProblem[]): void {
  for (const p of problems) {
    let send = plan.connections.find((c) => c.from === p.from && c.to === p.to);
    if (!send) {
      send = fixedConnection(
        model,
        model.rules.find((r) => r.from === p.from && r.to === p.to)!,
      );
      plan.connections.push(send);
    }
    send.params = { ...send.params, pan: p.pan };
  }
}

/** A COMP group whose 1-knob is on, holding an attack, a release or an Auto Makeup other than the
 *  one the knob gives it (`COMP_ONE_KNOB_ON`). Switching the knob on sets all three on the unit,
 *  and nothing on the unit moves them while it stays on, so the values a document carries are
 *  ones the unit never holds there — while the tuning screen, the MIDI feedback and the next save
 *  all read them. The loader sets them to the knob's values and says so.
 *
 *  Counted the way the write sees it: a key the document leaves out is the factory value the
 *  fill gives it, and a value of the wrong kind is one the range check drops to that value.
 *  Read on the plan as the linked-pair copy leaves it, since a STEREO-linked pair holds its COMP
 *  once. Like every check in this file it does NOT run on a device readback or the `.urxf`
 *  import: there the unit's own values arrive. */
export interface CompOneKnobProblem {
  reason: "compOneKnob";
  node: string;
  /** Each held key off the knob's value, with what the document carries there (absent where it
   *  names none) and the value the loader writes. */
  keys: { key: (typeof COMP_ONE_KNOB_HELD)[number]; stored?: unknown; value: number | boolean }[];
}

/** Every COMP group whose 1-knob is on and whose held keys are off the knob's values, in model
 *  order. */
export function compOneKnobProblems(model: DeviceModel, plan: Plan): CompOneKnobProblem[] {
  const out: CompOneKnobProblem[] = [];
  for (const node of model.nodes) {
    const np = plan.nodeParams[node.id];
    if (!channelDynamics(model, node.id, np?.compEqType ?? COMP_EQ_COMP_FIRST)?.comp) continue;
    const comp = (np?.comp ?? {}) as Record<string, unknown>;
    if (comp.oneKnob !== true) continue;
    const factory = (factoryNodeParams(model.id, node.id)?.comp ?? {}) as Record<string, unknown>;
    const keys: CompOneKnobProblem["keys"] = [];
    for (const key of COMP_ONE_KNOB_HELD) {
      const value = COMP_ONE_KNOB_ON[key];
      const stored = comp[key];
      const effective =
        typeof stored === typeof value && (typeof stored !== "number" || Number.isFinite(stored))
          ? stored
          : factory[key];
      if (effective === value) continue;
      keys.push({ key, ...(stored === undefined ? {} : { stored }), value });
    }
    if (keys.length > 0) out.push({ reason: "compOneKnob", node: node.id, keys });
  }
  return out;
}

/** Set each reported group's held keys to the knob's values. Separate from finding them for the
 *  reason `applyParamRange` is. */
export function applyCompOneKnobs(plan: Plan, problems: CompOneKnobProblem[]): void {
  for (const { node, keys } of problems) {
    const np = plan.nodeParams[node] ?? {};
    const comp = { ...np.comp } as Record<string, unknown>;
    for (const { key, value } of keys) comp[key] = value;
    plan.nodeParams[node] = { ...np, comp: comp as NodeParams["comp"] };
  }
}

/** An M.B.Comp whose 1-knob is on, holding a value other than the type's own in a slot the knob
 *  sets when it is switched on and holds while it stays on (`MBC_ONE_KNOB_FIXED`: each band's
 *  Attack and Bypass, the Release, both crossovers and the Out Gain). The write sends none of them
 *  while the knob is on, so the values a document carries are ones the unit never holds there —
 *  while the tuning screen, the MIDI feedback and the next save all read them. The loader sets
 *  them to the type's own values and says so.
 *
 *  Counted the way the write would send it: a value is held to its slot's window first, as the
 *  range check bounds it, and a slot the document leaves out is the type's own value the load
 *  completes it with. Like every check in this file it does NOT run on a device readback or the
 *  `.urxf` import: there the unit's own values arrive. */
export interface MbcOneKnobProblem {
  reason: "mbcOneKnob";
  node: string;
  /** Each fixed slot off the type's own value, with what the document carries there (under the
   *  family's key or the bare slot) and the value the loader writes. */
  slots: { slot: number; stored: unknown; value: number }[];
}

/** Every M.B.Comp whose 1-knob is on and whose fixed slots are off the type's own values, in model
 *  order. */
export function mbcOneKnobProblems(model: DeviceModel, plan: Plan): MbcOneKnobProblem[] {
  const out: MbcOneKnobProblem[] = [];
  for (const node of model.nodes) {
    if (!insertFxControl(model, node.id)) continue;
    const sel = effectiveInsertFx(model, plan, node.id);
    if (sel === undefined || insertFxFamilyOf(sel) !== "mbc") continue;
    const params = plan.nodeParams[node.id]?.insertFxParams as Record<string, unknown> | undefined;
    if (!insertFxDriverOn(params as Record<string, number> | undefined, "mbc", MBC_ONE_KNOB.on.slot)) continue;
    const windows = new Map(insertFxWritableSlots("mbc").map((w) => [w.slot, w]));
    const defs = insertFxDefaults("mbc", sel);
    const slots: MbcOneKnobProblem["slots"] = [];
    for (const slot of MBC_ONE_KNOB_FIXED) {
      const stored = params?.[`mbc:${slot}`] ?? params?.[String(slot)];
      const sent = insertFxSlotRaw(stored, windows.get(slot)!) ?? defs[slot];
      if (sent !== defs[slot]) slots.push({ slot, stored, value: defs[slot] });
    }
    if (slots.length > 0) out.push({ reason: "mbcOneKnob", node: node.id, slots });
  }
  return out;
}

/** Set each reported M.B.Comp's fixed slots to the type's own values, under the family's key.
 *  Separate from finding them for the reason `applyParamRange` is. */
export function applyMbcOneKnobs(plan: Plan, problems: MbcOneKnobProblem[]): void {
  for (const { node, slots } of problems) {
    const np = plan.nodeParams[node] ?? {};
    const patch = Object.fromEntries(slots.map(({ slot, value }) => [slot, value]));
    plan.nodeParams[node] = { ...np, insertFxParams: reKeyInsertFxParams(np.insertFxParams ?? {}, "mbc", patch) };
  }
}

/** A node colour that is no plan colour (`isPlanColor`) — not one of the unit's ten palette
 *  hexes, nor `COLOR_OFF`. The write sends nothing for one while every surface would paint
 *  whatever the string says, so the loader drops it — the fill then gives a colourable node its
 *  factory colour — and says so. Like every check in this file it does NOT run on a device
 *  readback, which writes palette colours only. */
export interface NodeColorProblem {
  reason: "nodeColor";
  node: string;
  /** The string the document carries. */
  stored: string;
}

/** Every node colour the plan holds that is no plan colour, in the plan's own order. */
export function nodeColorProblems(plan: Plan): NodeColorProblem[] {
  return Object.entries(plan.nodeColors)
    .filter(([, value]) => !isPlanColor(value))
    .map(([node, stored]) => ({ reason: "nodeColor" as const, node, stored }));
}

/** Drop each reported colour. Separate from finding them for the reason `applyParamRange` is. */
export function applyNodeColors(plan: Plan, problems: NodeColorProblem[]): void {
  for (const p of problems) delete plan.nodeColors[p.node];
}

/** A node name or a note the document carries in a form the app does not keep. A name is cut to
 *  what the unit's own CH SETTING screen takes and loses its trailing padding — the form every
 *  read of the unit gives it back in — and neither text keeps a code point XML refuses, since an
 *  image export serializes both into an SVG and one such character fails the whole export. The
 *  loader rewrites it and says so. A name read from the unit is not cleaned this way
 *  (`normalizeDocumentName`). Like every check in this file it does NOT run on a device readback. */
export interface DocumentTextProblem {
  reason: "documentText";
  field: "nodeNames" | "notes";
  node: string;
  /** The text the document carries. */
  stored: string;
  /** What the load keeps — empty where nothing is left, which for a name is no name. */
  value: string;
}

/** Every name and note the load rewrites, names first, each in the plan's own order. */
export function documentTextProblems(plan: Plan): DocumentTextProblem[] {
  const out: DocumentTextProblem[] = [];
  for (const [node, stored] of Object.entries(plan.nodeNames)) {
    const value = normalizeDocumentName(stored);
    if (value !== stored) out.push({ reason: "documentText", field: "nodeNames", node, stored, value });
  }
  for (const [node, stored] of Object.entries(plan.notes)) {
    const value = stripXmlInvalid(stored);
    if (value !== stored) out.push({ reason: "documentText", field: "notes", node, stored, value });
  }
  return out;
}

/** Write each reported text, removing a name nothing is left of. Separate from finding them for
 *  the reason `applyParamRange` is. */
export function applyDocumentText(plan: Plan, problems: DocumentTextProblem[]): void {
  for (const p of problems) {
    if (p.field === "nodeNames" && !p.value) delete plan.nodeNames[p.node];
    else plan[p.field][p.node] = p.value;
  }
}

/** An on/off leaf written as a number. The document sanitiser keeps any finite number, and the
 *  write sends one where an on/off belongs as on unless it is 0. The loader converts it to that
 *  on/off, so every reader of the plan holds a boolean there and reads what the write sends,
 *  and says so.
 *
 *  Which leaves are on/off is read off the model's factory values rather than listed here: a
 *  leaf the factory holds as a boolean is one, at the same path on the same node, an array
 *  element matched by its index. A leaf the factory does not carry is left alone. Like every
 *  check in this file it does NOT run on a device readback or the `.urxf` import, which write
 *  booleans. */
export interface BooleanParamProblem {
  reason: "booleanParam";
  node: string;
  /** The leaf inside the node's params, dotted, an array element by its index (`eqBands.0.on`). */
  path: string;
  /** The number the document carries. */
  stored: number;
  /** What the loader writes: off for 0, on for any other number. */
  value: boolean;
}

/** Every on/off leaf the plan holds as a number, node by node in the plan's order. */
export function booleanParamProblems(model: DeviceModel, plan: Plan): BooleanParamProblem[] {
  const out: BooleanParamProblem[] = [];
  for (const [node, params] of Object.entries(plan.nodeParams)) {
    const walk = (carried: unknown, factory: unknown, path: string[]): void => {
      if (typeof factory === "boolean") {
        if (typeof carried === "number")
          out.push({ reason: "booleanParam", node, path: path.join("."), stored: carried, value: carried !== 0 });
      } else if (Array.isArray(factory) && Array.isArray(carried)) {
        factory.forEach((f, i) => walk(carried[i], f, [...path, String(i)]));
      } else if (isPlainRecord(factory) && isPlainRecord(carried)) {
        for (const [key, f] of Object.entries(factory)) walk(carried[key], f, [...path, key]);
      }
    };
    walk(params, factoryNodeParams(model.id, node), []);
  }
  return out;
}

/** Write each reported leaf's on/off. Separate from finding them for the reason
 *  `applyParamRange` is. */
export function applyBooleanParams(plan: Plan, problems: BooleanParamProblem[]): void {
  for (const p of problems) {
    const keys = p.path.split(".");
    let holder = plan.nodeParams[p.node] as Record<string, unknown>;
    for (const key of keys.slice(0, -1)) holder = holder[key] as Record<string, unknown>;
    holder[keys[keys.length - 1]] = p.value;
  }
}

/** Everything a plan load reports: an illegal wire (refused), a slot claimed twice (the
 *  operator decides), a value outside its range (normalized, then reported), a receiver
 *  given no source (completed, then reported), a send listed without a level (completed,
 *  then reported), a linked pair whose members disagree (the primary copied onto the
 *  secondary, then reported), a linked send pan off its source's value (set to it, then
 *  reported), a COMP 1-knob that is on beside an attack, release or Auto Makeup it never runs
 *  with (set to the knob's, then reported), an M.B.Comp 1-knob that is on beside a fixed value
 *  other than the type's own (set to it, then reported), an on/off written as a number (converted, then reported), a colour that is no
 *  plan colour (dropped, then reported), or a name or note in a form the app does not keep
 *  (rewritten, then reported). */
export type LoadProblem =
  | PlanProblem
  | InsertFxSlotProblem
  | InsertFxPairProblem
  | ParamRangeProblem
  | RequiredSourceProblem
  | SendLevelProblem
  | LinkedPairProblem
  | LinkedSendPanProblem
  | CompOneKnobProblem
  | MbcOneKnobProblem
  | BooleanParamProblem
  | NodeColorProblem
  | DocumentTextProblem;

// Every violation the plan loader reports on a file / ?plan= link / drop, in one
// list so a load path cannot pick up half of them. The caller splits them by
// reason — a wire violation refuses the document, a slot collision only warns.
// Both halves check a plan built elsewhere; neither runs on a device readback.
// The on/off conversions come first and the other checks read the document as they leave
// it, which is the order the loader applies them in: `panLink: 1` is a linked MIX to the
// send-pan check, and `stereoLink: 1` a linked pair to the insert-FX pair check. The send-pan
// check reads it as the linked-pair copy leaves it too, since in BAL that copy moves the pans
// it compares against, and so does the COMP 1-knob check, since the copy carries a pair's COMP
// onto its secondary.
export function planProblems(model: DeviceModel, plan: Plan): LoadProblem[] {
  const booleans = booleanParamProblems(model, plan);
  const read = booleans.length > 0 ? structuredClone(plan) : plan;
  applyBooleanParams(read, booleans);
  const pairs = linkedPairProblems(model, read);
  const paired = pairs.length > 0 ? structuredClone(read) : read;
  applyLinkedPairs(model, paired, pairs);
  return [
    ...booleans,
    ...validatePlan(model, read),
    ...insertFxPairProblems(model, read),
    ...insertFxSlotProblems(model, read),
    ...paramRangeProblems(read),
    ...requiredSourceProblems(model, read),
    ...sendLevelProblems(model, read),
    ...pairs,
    ...linkedSendPanProblems(model, paired),
    ...compOneKnobProblems(model, paired),
    ...mbcOneKnobProblems(model, paired),
    ...nodeColorProblems(read),
    ...documentTextProblems(read),
  ];
}

/** Which side of that split a problem falls on: true refuses the document, false warns
 *  and offers to open it anyway. One seat, because the rule was written out three times
 *  — the loader, the report's caller and a test — and moving a reason between the sides
 *  in one of them would leave the others agreeing with the old split. */
export function isRefusal(problem: LoadProblem): boolean {
  return (
    problem.reason !== "insertFxSlot" &&
    problem.reason !== "paramRange" &&
    problem.reason !== "requiredSource" &&
    problem.reason !== "sendLevel" &&
    problem.reason !== "linkedPair" &&
    problem.reason !== "linkedSendPan" &&
    problem.reason !== "compOneKnob" &&
    problem.reason !== "mbcOneKnob" &&
    problem.reason !== "booleanParam" &&
    problem.reason !== "nodeColor" &&
    problem.reason !== "documentText"
  );
}

/** Whether a problem stops the load until the operator answers. A refusal does not — there
 *  is nothing to answer — and neither a normalized range, a completed source, a linked send
 *  pan set to its source's value, a COMP or M.B.Comp 1-knob's held values set to the knob's nor a
 *  converted on/off does: each is
 *  repaired before the document opens and reported on the status line, which is where
 *  architecture.md puts a partial success. Only the slot collision leaves a document the app
 *  can open and the unit cannot run, which is a decision and nobody else's. */
export function needsDecision(problem: LoadProblem): boolean {
  return problem.reason === "insertFxSlot";
}

/** What a load repaired, by kind. The status line says each kind in its own sentence. */
export interface LoadRepairs {
  booleans: BooleanParamProblem[];
  ranged: ParamRangeProblem[];
  supplied: RequiredSourceProblem[];
  sendLevels: SendLevelProblem[];
  linkedPairs: LinkedPairProblem[];
  linkedPans: LinkedSendPanProblem[];
  oneKnobs: CompOneKnobProblem[];
  mbcKnobs: MbcOneKnobProblem[];
  colors: NodeColorProblem[];
  texts: DocumentTextProblem[];
}

/** Apply every repair `planProblems` reported, in the order `planProblems` reads them: an on/off
 *  written as a number is converted first, then a value outside what the app can write is
 *  bounded or dropped, a receiver the unit never leaves without a source gets the one a new plan
 *  carries, a send listed without a level gets the one the write sends, a linked pair's
 *  secondary takes its primary's shared values, a send into a MIX whose Pan Link is on
 *  takes its source's own pan / balance, a COMP group whose 1-knob is on takes the knob's attack,
 *  release and Auto Makeup, an M.B.Comp whose 1-knob is on takes the type's own fixed values, a
 *  colour that is no plan colour is dropped, and a name
 *  or a note is rewritten to the form the app keeps.
 *  Refusals and decisions are the caller's; the reasons they carry are not repaired here. */
export function applyLoadRepairs(model: DeviceModel, plan: Plan, problems: LoadProblem[]): LoadRepairs {
  const booleans = problems.filter((p) => p.reason === "booleanParam");
  applyBooleanParams(plan, booleans);
  const ranged = problems.filter((p) => p.reason === "paramRange");
  applyParamRange(plan, ranged);
  const supplied = problems.filter((p) => p.reason === "requiredSource");
  applyRequiredSources(model, plan, supplied);
  const sendLevels = problems.filter((p) => p.reason === "sendLevel");
  applySendLevels(plan, sendLevels);
  const linkedPairs = problems.filter((p) => p.reason === "linkedPair");
  applyLinkedPairs(model, plan, linkedPairs);
  const linkedPans = problems.filter((p) => p.reason === "linkedSendPan");
  applyLinkedSendPans(model, plan, linkedPans);
  const oneKnobs = problems.filter((p) => p.reason === "compOneKnob");
  applyCompOneKnobs(plan, oneKnobs);
  const mbcKnobs = problems.filter((p) => p.reason === "mbcOneKnob");
  applyMbcOneKnobs(plan, mbcKnobs);
  const colors = problems.filter((p) => p.reason === "nodeColor");
  applyNodeColors(plan, colors);
  const texts = problems.filter((p) => p.reason === "documentText");
  applyDocumentText(plan, texts);
  return { booleans, ranged, supplied, sendLevels, linkedPairs, linkedPans, oneKnobs, mbcKnobs, colors, texts };
}

/** Give each node's selected insert effect every engine slot it leaves out, at that type's own
 *  default (`seedInsertFxParams`), the effect being the one the write acts on
 *  (`effectiveInsertFx`). Returns the contest names it set. The factory values carry no engine
 *  values, so this is the half of the completion `fillFactoryParams` cannot supply. */
export function completeInsertFxParams(model: DeviceModel, plan: Plan): string[] {
  const names: string[] = [];
  for (const node of model.nodes) {
    const np = plan.nodeParams[node.id];
    if (!insertFxControl(model, node.id) || np?.insertFx === undefined) continue;
    const selector = effectiveInsertFx(model, plan, node.id);
    if (selector === undefined) continue;
    const { params, seeded } = seedInsertFxParams(np.insertFxParams, selector);
    if (seeded.length === 0) continue;
    plan.nodeParams[node.id] = { ...np, insertFxParams: params };
    for (const key of seeded) names.push(nodeParamContestPath(node.id, `insertFxParams.${key}`));
  }
  return names;
}

/** Give each nameable node a document leaves unnamed — no entry, or an empty one — the model's
 *  factory name. A name the write does not send is one the unit keeps as it has it, while every
 *  surface draws the node's label there, so the load supplies the name the write then sends.
 *  Returns the contest names it set. */
export function completeNodeNames(model: DeviceModel, plan: Plan): string[] {
  const factory = factoryNodeNames(model.id);
  const names: string[] = [];
  for (const node of model.nodes) {
    if (plan.nodeNames[node.id] || !nameControl(model, node.id) || !factory[node.id]) continue;
    plan.nodeNames[node.id] = factory[node.id];
    names.push(nodeNameContestKey(node.id));
  }
  return names;
}

/** Give each colourable node a document leaves without a colour the model's factory colour, so
 *  absent no longer means "leave the unit alone" — the write sends a colour for every node it can.
 *  Returns the contest names it set. */
export function completeNodeColors(model: DeviceModel, plan: Plan): string[] {
  const factory = factoryNodeColors(model.id);
  const names: string[] = [];
  for (const node of model.nodes) {
    if (plan.nodeColors[node.id] !== undefined || !colorControl(model, node.id) || !factory[node.id]) continue;
    plan.nodeColors[node.id] = factory[node.id];
    names.push(nodeColorContestKey(node.id));
  }
  return names;
}

/** A document as the loader opens it: its own wire params, names and colours recorded as the
 *  document's, repaired (`applyLoadRepairs`), then completed from the model's factory values — a
 *  value a repair dropped is completed like any other absent one — each selected insert effect
 *  from its type's defaults, and each unnamed or uncoloured node from its factory name or colour,
 *  all recorded as the fill's, then put back through the rate rule, which a Track Count the fill
 *  completes can exceed. */
export function prepareLoadedPlan(model: DeviceModel, plan: Plan, problems: LoadProblem[]): LoadRepairs {
  // The document's own wire params and names are what it wrote, as its node params are (the
  // fill records those); a repair below that completes one records its own.
  const source = (plan.paramSource ??= new Map());
  for (const [id, name] of Object.entries(plan.nodeNames)) {
    if (name && !source.has(nodeNameContestKey(id))) source.set(nodeNameContestKey(id), "load");
  }
  for (const id of Object.keys(plan.nodeColors)) {
    if (!source.has(nodeColorContestKey(id))) source.set(nodeColorContestKey(id), "load");
  }
  for (const c of plan.connections) {
    for (const key of Object.keys(c.params ?? {})) {
      const name = connParamContestKey(c.from, c.to, key);
      if (!source.has(name)) source.set(name, "load");
    }
  }
  const repairs = applyLoadRepairs(model, plan, problems);
  fillFactoryParams(model.id, plan);
  for (const name of completeInsertFxParams(model, plan)) source.set(name, "default");
  for (const name of completeNodeNames(model, plan)) source.set(name, "default");
  for (const name of completeNodeColors(model, plan)) source.set(name, "default");
  setPlanSampleRate(plan, plan.sampleRate);
  return repairs;
}
